import React, { memo, useCallback, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { IconButton as IconButtonOld, type IconButtonSpacing } from '@atlaskit/button/new';
import DeleteIcon from '@atlaskit/icon/core/delete';
import SettingsIconNew from '@atlaskit/icon/core/settings';
import { MenuGroup, LinkItem, Section, ButtonItem } from '@atlaskit/menu';
import type { Placement } from '@atlaskit/popper';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import type { Board } from '@atlassian/jira-boards-common/src/model/index.tsx';
import DeleteModal from '@atlassian/jira-delete-boards-modal/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { isBacklogRoute } from '@atlassian/jira-router-resources-classic-projects/src/services/data-api/utils/is-route.tsx';
import {
	BOARD_SETTINGS,
	REMOVE_FROM_STARRED,
	ADD_TO_STARRED,
	DELETE_BOARD,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import { useProjectsAnalyticsL3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l3/index.tsx';
import { useProjectsAnalyticsL4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l4/index.tsx';
import { FavoriteToggleButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/favorite-toggle-button/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { BoardActions$key } from '@atlassian/jira-relay/src/__generated__/BoardActions.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { ThemedIconButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import {
	getBoardUrl,
	getBoardSettingsUrl,
} from '@atlassian/jira-software-board-settings-rapidboard-config-common/src/common/url-utils/index.tsx';
import { useRoute } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/use-route/index.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import messages from './messages.tsx';

export type BoardWithDataId = Board & { dataId: string };

type BoardActionsProps = {
	board?: BoardWithDataId | null;
	projectFragment: BoardActions$key | null;
	placement?: Placement;
	isBoardFavorite?: boolean | undefined;
	/** Force the popup to start open, used for testing only */
	forceOpen?: boolean;
	isParentProjectStarred?: boolean | null | undefined;
	iconButtonSpacing?: IconButtonSpacing;
	themed?: boolean; // Board actions should not be themed in the sidebar, but can be in the header
};

/**
 * The BoardActions menu with options Board Settings and Delete Board.
 * It is displayed in the project board header when there is more than one board in the project
 * and in the sidebar next to each board.
 */
export const BoardActions = memo(
	({
		board,
		projectFragment,
		placement,
		isBoardFavorite = false,
		isParentProjectStarred,
		forceOpen = false,
		iconButtonSpacing = 'default',
		themed = false,
	}: BoardActionsProps): React.JSX.Element | null => {
		const [isOpen, setIsOpen] = useState(forceOpen);
		const { formatMessage } = useIntl();
		const { sendMeatBallDropDownOnBoardsAnalyticsEvent } = useProjectsAnalyticsL3();
		const { sendMeatBallDropDownOnBoardsMenuItemAnalyticsEvent } = useProjectsAnalyticsL4();

		const project = useFragment(
			graphql`
				fragment BoardActions on JiraProject {
					projectKey: key
					projectId
					projectType
				}
			`,
			projectFragment,
		);

		const tenantId = useCloudId();
		const boardARI = JiraSoftwareBoardAri.create({
			siteId: tenantId,
			boardId: board?.id.toString() ?? '',
		}).toString();

		const [showDeleteModal, setShowDeleteModal] = useState(false);
		const boardId = board?.id || '';
		const { push } = useRouterActions();
		const route = useRoute();
		const isBacklogUrl = isBacklogRoute(route.name);

		const openModal = () => {
			setShowDeleteModal(true);
			sendMeatBallDropDownOnBoardsMenuItemAnalyticsEvent({
				recent: !isParentProjectStarred,
				starred: isParentProjectStarred ?? false,
				boardId: Number(boardId),
				itemId: DELETE_BOARD,
				projectId: project?.projectId ?? '',
				projectType: project?.projectType,
			});
		};

		const handleBoardDelete = () => {
			if (!board?.dataId) return;
			setShowDeleteModal(false);
			push(`/browse/${project?.projectKey}`);
		};

		const handleModalCancel = () => {
			setShowDeleteModal(false);
		};

		const IconButton = themed && getIsSoftwareThemingM2Enabled() ? ThemedIconButton : IconButtonOld;

		const onBoardDeleteFailed = useCallback(() => setShowDeleteModal(false), []);

		const onTriggerClick = useCallback(() => {
			setIsOpen(!isOpen);
			sendMeatBallDropDownOnBoardsAnalyticsEvent({
				recent: !isParentProjectStarred,
				starred: isParentProjectStarred ?? false,
				boardId: Number(boardId),
				projectId: project?.projectId ?? '',
				projectType: project?.projectType,
			});
		}, [
			isOpen,
			sendMeatBallDropDownOnBoardsAnalyticsEvent,
			isParentProjectStarred,
			boardId,
			project?.projectId,
			project?.projectType,
		]);

		const onClose = useCallback(() => setIsOpen(false), []);

		if (!project?.projectKey) return null;

		return (
			<Popup
				isOpen={isOpen}
				onClose={onClose}
				placement={placement ?? 'bottom-start'}
				testId="navigation-board-action-menu.ui.popup"
				messageId="navigation-board-action-menu.ui.popup"
				messageType="transactional"
				content={() => (
					<>
						<MenuGroup>
							<Section>
								<FavoriteToggleButton
									entityId={boardARI}
									entityTypeName="JiraBoard"
									defaultIsStarred={isBoardFavorite}
									onClick={() =>
										sendMeatBallDropDownOnBoardsMenuItemAnalyticsEvent({
											recent: !isParentProjectStarred,
											starred: isParentProjectStarred ?? false,
											itemId: isBoardFavorite ? REMOVE_FROM_STARRED : ADD_TO_STARRED,
											boardId: Number(boardId),
											projectId: project?.projectId ?? '',
											projectType: project?.projectType,
										})
									}
								/>
								<LinkItem
									iconBefore={
										<SettingsIconNew label="" color={token('color.icon')} spacing="spacious" />
									}
									aria-label={formatMessage(messages.boardSettingsLabel)}
									href={getJSWCMPBoardSettingsUrl(project?.projectKey, boardId, isBacklogUrl)}
									onClick={() => {
										sendMeatBallDropDownOnBoardsMenuItemAnalyticsEvent({
											recent: !isParentProjectStarred,
											starred: isParentProjectStarred ?? false,
											boardId: Number(boardId),
											itemId: BOARD_SETTINGS,
											projectId: project?.projectId ?? '',
											projectType: project?.projectType,
										});
									}}
								>
									{formatMessage(messages.boardSettingsLabel)}
								</LinkItem>
							</Section>
							{board?.canEdit && (
								<Section hasSeparator>
									<ButtonItem
										iconBefore={
											<DeleteIcon
												label=""
												spacing="spacious"
												color={token('color.icon.accent.red')}
											/>
										}
										onClick={openModal}
									>
										<Text color="color.text.danger">
											{formatMessage(messages.deleteBoardLabel)}
										</Text>
									</ButtonItem>
								</Section>
							)}
						</MenuGroup>
						{board?.canEdit && showDeleteModal && (
							<DeleteModal
								board={board}
								onBoardDeleteFailed={onBoardDeleteFailed}
								onBoardDeleted={handleBoardDelete}
								onCancel={handleModalCancel}
							/>
						)}
					</>
				)}
				trigger={(triggerProps) => (
					<IconButton
						{...triggerProps}
						icon={(iconProps) => <ShowMoreHorizontalIcon {...iconProps} size="small" />}
						label={formatMessage(messages.dropdownIconLabel)}
						appearance="subtle"
						spacing={iconButtonSpacing}
						onClick={onTriggerClick}
						isSelected={isOpen}
						testId="navigation-board-action-menu.ui.dropdown"
					/>
				)}
				shouldRenderToParent
			/>
		);
	},
);

function getJSWCMPBoardSettingsUrl(projectKey: string, boardId: string, isBacklogUrl: boolean) {
	const query = isBacklogUrl ? '?from-backlog=1' : '';
	return `${getBoardSettingsUrl(getBoardUrl(projectKey, boardId, true))}/filter${query}`;
}
